import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useBoolean } from "@/hooks/use-boolean";
import capitalize from "@/utils/capitalize";
import useUpdateShopView from "@babackapp/private-api-client/client/use-update-shop-view";
import { ShopViewDto } from "@babackapp/private-api-client/types";
import FormInput from "@babackapp/react-hook-forms-components/form-input";
import FormMultiOptionsSelector from "@babackapp/react-hook-forms-components/form-multi-options-selector";
import FormSaveButton from "@babackapp/react-hook-forms-components/form-save-button";
import useTranslation from "@babackapp/translations/use-translation";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@babackapp/ui/components/dialog";
import { Form, FormField } from "@babackapp/ui/components/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { PropsWithChildren } from "react";
import { useForm, useFormContext } from "react-hook-form";
import { z } from "zod";
import { columnIds } from "../../hooks/use-return-requests-table";
import { mapShopViewToTableColumns } from "../../hooks/use-shop-views";

const formSchema = z.object({
  name: z.string().min(1),
  fields: z.array(z.enum(columnIds)),
});

type FormValues = z.infer<typeof formSchema>;

type UpdateShopViewDialogProps = {
  shopView: ShopViewDto;
};

export default function UpdateShopViewDialog({
  children,
  shopView,
}: PropsWithChildren<UpdateShopViewDialogProps>) {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const open = useBoolean();
  const { mutateAsync: updateShopView } = useUpdateShopView(
    shop.id,
    shopView.id,
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: async () => {
      const fields = mapShopViewToTableColumns(shopView)
        .fields.filter((field) => field.active)
        .map((field) => field.name) as FormValues["fields"];

      return {
        name: shopView.name,
        fields: [...new Set(fields)],
      };
    },
  });

  const onSubmit = form.handleSubmit(async ({ name, fields }) => {
    await updateShopView({
      name,
      fields: fields.map((field) => ({ name: field, active: true })),
    });
    open.setFalse();
  });

  return (
    <Dialog open={open.value} onOpenChange={open.setValue}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("shopViewFilter.updateDialog.title", {
              view: capitalize(shopView.name),
            })}
          </DialogTitle>
          <DialogDescription>
            {t("shopViewFilter.updateDialog.description")}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            className="flex flex-col gap-8 min-h-52 mt-4"
            onSubmit={onSubmit}
          >
            <NameInput />
            <FieldsSelector />
            <FormSaveButton />
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function NameInput() {
  const t = useTranslation();
  const { control } = useFormContext<FormValues>();

  return (
    <FormField
      name="name"
      control={control}
      render={({ field }) => (
        <FormInput
          label={t("shopViewFilter.form.name.title")}
          description={t("shopViewFilter.form.name.description")}
          onChange={field.onChange}
          value={field.value}
        />
      )}
    />
  );
}

function FieldsSelector() {
  const t = useTranslation();
  const { control } = useFormContext<FormValues>();

  return (
    <FormField
      name="fields"
      control={control}
      render={({ field }) => (
        <FormMultiOptionsSelector
          label={t("shopViewFilter.form.fields.title")}
          description={t("shopViewFilter.form.fields.description")}
          onChange={field.onChange}
          values={field.value}
          options={columnIds.map((id) => ({
            key: id,
            label: id,
          }))}
        />
      )}
    />
  );
}
